// §4.17 MERGE — the preview with its choices and Confirm (Module 09, P3-E2 PR
// E2-2b; spec 09 §3.13 and §4.17, D-324 rulings 4 to 11).
//
// THIS COMPONENT OWNS THE ADMIN'S DECISIONS: the side per field, the cleared
// ticks, and what happened to the last Confirm. It is keyed on the pair by the
// screen, so picking another customer starts every choice again on the
// winner. A re-read of the SAME pair (after `MERGE_STALE`) keeps the choices
// the rules still allow (`reconcileChoices`, `reconcileTicks`), because the
// admin decided them and only the facts under them moved.
//
// WHAT EACH REFUSAL SAYS (§4.17, "every error states what happened and what to
// do"). The merge writes nothing on any refusal (§3.13), so every note below
// is a fact about the press, never about partial work.
//  * 409 `MERGE_STALE` reads the preview again and lists what moved, from the
//    two previews (`staleChanges`): a customer's version, the access set or
//    the commercial set. Nothing visible may have moved (a write that left
//    every shown value as it was still moves the row's version), and then the
//    list is empty and the sentence alone says the preview was read again.
//  * 409 `MERGE_TAX_ID_CONFLICT`, `MERGE_CURRENCY_MISMATCH` or
//    `MERGE_IMPORT_ORDER_REF_CONFLICT` means a blocker arose after the read,
//    so the preview is read again and shows it, and Confirm turns off with
//    the blocker named.
//  * 503 `RESOURCE_BUSY` (a writer held a moved row, §9 item 33) is tried
//    again ONCE after the server's `Retry-After`, two seconds when it names
//    none, with the same key and body, and then the page says the customers
//    are busy. The same key is safe: a 5xx releases it (merge-queries.ts).
//  * 400 on `tax_id` (`tax_id_format`, the final country refuses the final tax
//    id) sits on the tax id's row and changes no choice. It clears once the
//    admin picks another tax id or country.
//  * Everything else is the area's failure note with the server's title and
//    the request id, under a sentence of ours where we know the code.

import { useEffect, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../../components/button";
import { RequestError, newIdempotencyKey } from "../../../../../lib/request";
import { MutationFailureNote } from "../../mutation-failure-note";
import { useReadsHeldOffline } from "../../lib/reads";
import {
  useMergeCustomers,
  type MergeFieldChoices,
  type MergePreview,
  type MergeRequest,
} from "../../hooks/merge-queries";
import {
  choiceRules,
  chooseSide,
  confirmAllowed,
  defaultChoices,
  mergeBody,
  reconcileChoices,
  reconcileTicks,
  staleChanges,
  type StaleChange,
} from "./merge-model";
import {
  MergeAccessList,
  MergeBlockers,
  MergeCommercialFiles,
  MergeFieldChoicesTable,
  MergeKindCounts,
} from "./merge-preview";

/** Two seconds when the server names no `Retry-After` (§3.13's own value). */
const BUSY_RETRY_MS = 2_000;

/** The refusals that mean "read the preview again" (see the file comment). */
const REREAD_CODES: ReadonlySet<string> = new Set([
  "MERGE_STALE",
  "MERGE_TAX_ID_CONFLICT",
  "MERGE_CURRENCY_MISMATCH",
  "MERGE_IMPORT_ORDER_REF_CONFLICT",
]);

type Outcome =
  | { readonly kind: "none" }
  | { readonly kind: "retrying" }
  | { readonly kind: "busy" }
  | { readonly kind: "reread"; readonly code: string; readonly base: MergePreview }
  | { readonly kind: "taxId" }
  | { readonly kind: "failed"; readonly error: unknown };

/** `extensions.missing_permission`, at the top level or nested. */
function missingPermissionOf(error: RequestError): string | undefined {
  const problem = error.problem as Record<string, unknown> | undefined;
  const direct = problem?.["missing_permission"];
  if (typeof direct === "string") return direct;
  const extensions = problem?.["extensions"];
  if (typeof extensions === "object" && extensions !== null) {
    const nested = (extensions as Record<string, unknown>)["missing_permission"];
    if (typeof nested === "string") return nested;
  }
  return undefined;
}

/** The area's own sentence for a refusal it knows, else the generic one. */
export function mergeRefusalText(
  error: unknown,
  t: ReturnType<typeof useTranslation>["t"],
): string {
  if (!(error instanceof RequestError)) return t("sales.merge.failed");
  if (error.code === "MERGE_CUSTOMER_ARCHIVED" || error.code === "MERGE_SAME_CUSTOMER") {
    return t(`sales.merge.refused.${error.code}`);
  }
  if (error.httpStatus === 403) {
    return missingPermissionOf(error) === "sales.tier.manage"
      ? t("sales.merge.refused.tier")
      : t("sales.merge.refused.forbidden");
  }
  if (error.httpStatus === 404) return t("sales.merge.refused.notFound");
  return t("sales.merge.failed");
}

export function MergePanel({
  preview,
  rereading,
  mayChangeTier,
  onReread,
}: {
  preview: MergePreview;
  /** The screen is reading the preview again. */
  rereading: boolean;
  /** The reader holds `sales.tier.manage`. */
  mayChangeTier: boolean;
  /** Read the preview again (a new key, so a new answer). */
  onReread: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const merge = useMergeCustomers();
  const offline = useReadsHeldOffline();
  const rules = choiceRules(preview, mayChangeTier);
  const [choices, setChoices] = useState<MergeFieldChoices>(() =>
    reconcileChoices(defaultChoices(), rules),
  );
  const [cleared, setCleared] = useState<ReadonlySet<string>>(new Set());
  const [outcome, setOutcome] = useState<Outcome>({ kind: "none" });
  const retryTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // A re-read of the same pair: keep what the rules still allow.
  const [seen, setSeen] = useState(preview);
  if (seen !== preview) {
    setSeen(preview);
    setChoices((prior) => reconcileChoices(prior, choiceRules(preview, mayChangeTier)));
    setCleared((prior) => reconcileTicks(preview, prior));
  }

  useEffect(
    () => () => {
      if (retryTimer.current !== undefined) clearTimeout(retryTimer.current);
    },
    [],
  );

  const busy = merge.isPending || outcome.kind === "retrying" || rereading;

  const send = (body: MergeRequest, key: string, retried: boolean) => {
    merge.mutate(
      { winnerId: preview.winner.id, body, idempotencyKey: key },
      {
        onSuccess: (result) => {
          setOutcome({ kind: "none" });
          void navigate({
            to: "/sales/customers/$customerId",
            params: { customerId: result.winner_customer_id },
          });
        },
        onError: (error) => {
          if (error instanceof RequestError && error.code === "RESOURCE_BUSY") {
            if (retried) {
              setOutcome({ kind: "busy" });
              return;
            }
            setOutcome({ kind: "retrying" });
            retryTimer.current = setTimeout(() => {
              retryTimer.current = undefined;
              send(body, key, true);
            }, error.retryAfterMs ?? BUSY_RETRY_MS);
            return;
          }
          if (
            error instanceof RequestError &&
            error.code !== undefined &&
            REREAD_CODES.has(error.code)
          ) {
            setOutcome({ kind: "reread", code: error.code, base: preview });
            onReread();
            return;
          }
          if (
            error instanceof RequestError &&
            error.httpStatus === 400 &&
            error.fieldErrors.some((entry) => entry.path === "tax_id")
          ) {
            setOutcome({ kind: "taxId" });
            return;
          }
          setOutcome({ kind: "failed", error });
        },
      },
    );
  };

  const confirm = () => {
    setOutcome({ kind: "none" });
    send(mergeBody(preview, choices, cleared), newIdempotencyKey(), false);
  };

  const changes: StaleChange[] =
    outcome.kind === "reread" && outcome.base !== preview
      ? staleChanges(outcome.base, preview)
      : [];

  return (
    <div className="sales-merge" data-testid="sales-merge-preview">
      <MergeFieldChoicesTable
        preview={preview}
        choices={choices}
        rules={rules}
        taxIdRefused={outcome.kind === "taxId"}
        onChoose={(field, side) => {
          setChoices((prior) => chooseSide(prior, field, side, rules));
          // The tax id's refusal named the pair the admin had chosen; a new
          // choice of either is the remedy it asked for.
          if (outcome.kind === "taxId" && (field === "tax_id" || field === "country")) {
            setOutcome({ kind: "none" });
          }
        }}
      />
      <MergeKindCounts
        preview={preview}
        tierChanged={choices.tier === "loser" && rules.tiersDiffer}
      />
      <MergeAccessList
        preview={preview}
        cleared={cleared}
        disabled={busy}
        onToggle={(userId, keep) => {
          setCleared((prior) => {
            const next = new Set(prior);
            if (keep) next.delete(userId);
            else next.add(userId);
            return next;
          });
        }}
      />
      <MergeCommercialFiles preview={preview} />
      <MergeBlockers preview={preview} />

      {outcome.kind === "reread" && outcome.code === "MERGE_STALE" && !rereading ? (
        <div className="sales-merge-section" role="status" data-testid="sales-merge-stale">
          <p className="sales-hint">{t("sales.merge.staleTitle")}</p>
          {changes.length > 0 ? (
            <ul className="sales-merge-stale">
              {changes.map((change) => (
                <li key={change} data-testid="sales-merge-stale-change" data-change={change}>
                  {t(`sales.merge.stale.${change}`)}
                </li>
              ))}
            </ul>
          ) : null}
          <p className="sales-hint">{t("sales.merge.staleHint")}</p>
        </div>
      ) : null}

      <div className="sales-merge-actions">
        <Button
          variant="primary"
          data-testid="sales-merge-confirm"
          busy={merge.isPending || outcome.kind === "retrying"}
          busyLabel={t("sales.merge.confirming")}
          disabled={!confirmAllowed(preview, offline, busy)}
          onClick={confirm}
        >
          {t("sales.merge.confirm", { name: preview.winner.display_name })}
        </Button>
        {offline ? (
          <p className="sales-hint" role="status" data-testid="sales-merge-offline">
            {t("sales.merge.offline")}
          </p>
        ) : null}
      </div>

      {outcome.kind === "retrying" ? (
        <p className="sales-hint" role="status" data-testid="sales-merge-retrying">
          {t("sales.merge.busyRetrying")}
        </p>
      ) : null}
      {outcome.kind === "busy" ? (
        <p className="sales-error-note" role="alert" data-testid="sales-merge-busy">
          {t("sales.merge.busy")}
        </p>
      ) : null}
      {outcome.kind === "failed" ? (
        <MutationFailureNote
          data-testid="sales-merge-failed"
          text={mergeRefusalText(outcome.error, t)}
          error={outcome.error}
        />
      ) : null}
    </div>
  );
}
